<template>
  <div>
    <h1>Earns Methoden</h1>
    <p>
      Maßnahmen können mit der überschlägigen oder der genormten / wissenschaftlichen 
      Methode berechnet werden. Die überschlägigen Methoden benötigen weniger Daten 
      und sind somit schneller zu untersuchen. Die genormten / wissenschaftlichen Methoden 
      bieten auf der anderen Seite eine zuverlässige Berechnung welche belastbar sind.
    </p>
    <p>
      Um eine Maßnahme abspeichern zu können, sind in alle technischen
      Pflichtfelder valide Werte einzutragen. Zusätzlich können optional die
      finanziellen Parameter eingetragen werden.
    </p>
    <h2>Beleuchtung</h2>

    <v-expansion-panels accordion class="mt-5">
         <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Eingabe der Bestandsanlage (Referenz) und der geplanten Anlage</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <table>
            <caption>Übergreifend</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Zone</td>
                <td>Auswahl der Gebäudezone.</td>
              </tr>
              <tr>
                <td>Fläche</td>
                <td>Eingabe der Fläche.</td>
              </tr>
              <tr>
                <td>Austausch</td>
                <td>Auswahl der Austauschoption.</td>
              </tr>
            </tbody>
          </table>
          <table>
            <caption>Parameter der Anlage</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Manuelle Brenndauer</td>
                <td>Option manuelle Brenndauer.</td>
              </tr>
              <tr>
                <td>Brenndauer</td>
                <td>Eingabe der Brenndauer, wenn die Berechnete von der Norm abweicht.</td>
              </tr>
              <tr>
                <td>Zonenprofil manuell</td>
                <td>
                  Zwölf Nutzungszonen haben kein Nutzungsprofil in DIN V 18599-10,
                  Anhang A: die neun Industrie-Schichtzonen, beide Parkplatzzonen und
                  „Sonstige“. Für sie sind die Betriebszeiten nach DIN V 18599-4, 5.3
                  gesondert zu ermitteln und hier einzutragen. Das Ergebnis weist dann
                  aus, dass das Profil nicht aus der Norm stammt.
                </td>
              </tr>
              <tr>
                <td>Nutzungsstunden zur Tag- und zur Nachtzeit</td>
                <td>
                  t<sub>Tag</sub> und t<sub>Nacht</sub> in h/a. Die Trennung ist nötig,
                  weil die Tageslichtgutschrift nur auf die Tagstunden wirkt.
                </td>
              </tr>
              <tr>
                <td>Minderungsfaktor Gebäudebetriebszeit</td>
                <td>
                  F<sub>t</sub> &ndash; welcher Anteil der Betriebszeit tatsächlich
                  beleuchtet wird. Zu beachten: die Normbrenndauer der Zonen enthält
                  diesen Faktor bereits, die hier einzutragenden Stunden dagegen nicht.
                </td>
              </tr>
              <tr>
                <td>Relative Abwesenheit</td>
                <td>
                  C<sub>A</sub> &ndash; Anteil der Nutzungszeit ohne Anwesenheit. Wirkt
                  nur zusammen mit einem Präsenzmelder; ohne einen bleibt der Wert
                  folgenlos.
                </td>
              </tr>
              <tr>
                <td>Freie Wahl der Leuchte</td>
                <td>
                  Hebt die Einschränkung des Leuchtenkatalogs auf die gewählte Zone auf.
                  Zu setzen, wenn die tatsächlich verbaute Leuchte in der Zonenliste
                  nicht vorkommt. Die Trennung zwischen Bestands- und Neutechnik bleibt
                  bestehen.
                </td>
              </tr>
              <tr>
                <td>Leuchte</td>
                <td>Auswahl des Leuchtentyps.</td>
              </tr>
              <tr>
                <td>Anzahl</td>
                <td>
                    Anzahl der Leuchten des gleichen Typs und der gleichen Betriebsweise.
                </td>
              </tr>
              <tr>
                <td>Freie Leistung</td>
                <td>
                  Lässt die Leistung je Lampe frei eintragen, statt sie aus dem Katalog
                  zu wählen &ndash; für Lampen, deren Leistung der Katalog nicht führt.
                  Die Verlustleistung des Betriebsgeräts wird darauf weiterhin addiert.
                  Nicht zu verwechseln mit der Systemleistung je Leuchte weiter unten,
                  die für die <em>ganze</em> Leuchte gilt und nichts mehr hinzurechnet.
                </td>
              </tr>
              <tr>
                <td>Leistung je Lampe</td>
                <td>
                  Leistung einer einzelnen Lampe, aus dem Katalog gewählt &ndash; oder
                  frei eingetragen, wenn „Freie Leistung“ gesetzt ist.
                </td>
              </tr>
              <tr>
                <td>Lampen je Leuchte</td>
                <td>
                  Anzahl der Lampen in einer Leuchte — eine zweiflammige Deckenleuchte
                  hat zwei.
                </td>
              </tr>
              <tr>
                <td>Ermittlung der Systemleistung</td>
                <td>
                  Welcher der drei Wege nach DIN V 18599-4, 5.4.5 gerechnet wird &ndash;
                  siehe „Systemleistung einer Leuchte“ im Abschnitt
                  <em>Berechnungsgrundlage und Daten</em>. Die Vorgabe ist das bisherige
                  EARNS-Verfahren, damit gespeicherte Ergebnisse unverändert bleiben.
                </td>
              </tr>
              <tr>
                <td>Systemleistung je Leuchte</td>
                <td>
                  Nur beim Weg „laut Hersteller oder Messung“. Gesamte
                  Leistungsaufnahme <strong>einer Leuchte</strong> in W, Betriebsgerät
                  eingeschlossen. Lampenleistung, Lampen je Leuchte und Verlustleistung
                  gehen dann nicht mehr ein &ndash; die Angabe enthält sie bereits.
                </td>
              </tr>
              <tr>
                <td>Vorschaltgerät</td>
                <td>
                  Betriebsgerät der Lampe. Bei LED-Leuchten ist der Treiber bereits in
                  der Katalogleistung enthalten und wird nicht ein zweites Mal addiert.
                  Die Wahl bestimmt außerdem, welche Steuerungen möglich sind: nur ein
                  regelbares Gerät trägt Tageslichtregelung und Konstantlichtregelung.
                </td>
              </tr>
              <tr>
                <td>Vorschaltgeräte je Leuchte</td>
                <td>
                  Nicht zwingend gleich der Lampenzahl: ein elektronisches
                  Vorschaltgerät kann zwei Lampen betreiben, ein konventionelles nicht.
                </td>
              </tr>
            </tbody>
          </table>
          <table>
            <caption>Regelung nach DIN V 18599-4 (neue Rechenmethode)</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Präsenzmelder</td>
                <td>
                  Ob die Beleuchtung anwesenheitsabhängig geschaltet wird. Wie viel das
                  bringt, hängt stark von der Zone ab — im ständig belegten Großraumbüro
                  nichts, im Lager bis zu 86&nbsp;%. Die Norm unterstellt auch
                  <em>ohne</em> Melder, dass von Hand ausgeschaltet wird.
                </td>
              </tr>
              <tr>
                <td>Tageslichtversorgung</td>
                <td>
                  Einstufung in keine / gering / mittel / gut — die wichtigste Eingabe
                  dieses Abschnitts. „keine“ schaltet die Tageslichtgutschrift ab; alle
                  weiteren Tageslichtfelder bleiben dann ohne Wirkung.
                </td>
              </tr>
              <tr>
                <td>Tageslichtabhängige Steuerung</td>
                <td>
                  Manuell, gedimmt ohne Abschaltung oder gedimmt mit Abschaltung. Die
                  Norm fasst ihre sieben Systemarten auf diese drei zusammen. Die beiden
                  gedimmten Stufen setzen ein regelbares Betriebsgerät voraus und sind
                  sonst gesperrt &ndash; ein nur schaltbares Gerät kann nicht dimmen.
                </td>
              </tr>
              <tr>
                <td>Anteil tageslichtversorgte Fläche</td>
                <td>
                  Welcher Teil der Zone überhaupt Tageslicht bekommt (0 bis 1).
                  Tageslicht wirkt nur auf diesen Anteil und nur auf die Tagstunden.
                  Der Vorgabewert 1,0 unterstellt, dass die ganze Zone versorgt ist —
                  bei tiefen Räumen zu prüfen.
                </td>
              </tr>
              <tr>
                <td>Konstantlichtregelung</td>
                <td>
                  Hält die Beleuchtungsstärke über die Wartungsdauer konstant, statt
                  anfangs zu überbeleuchten. Nur mit regelbarem Betriebsgerät wählbar.
                </td>
              </tr>
            </tbody>
          </table>

          <table>
            <caption>Regelung — Details (die Vorgabewerte genügen im Regelfall)</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Art der Tageslichtöffnung</td>
                <td>
                  Vertikale Fassade oder Dachoberlicht. Die Norm rechnet beide nach
                  getrennten Verfahren; bei Dachoberlichtern gehen Verglasung und
                  Sonnenschutz nicht gesondert ein.
                </td>
              </tr>
              <tr>
                <td>Orientierung</td>
                <td>
                  Süd, Ost/West oder Nord. Über das Jahr macht das kaum einen
                  Unterschied: Nord bekommt weniger Sonne, dafür ist dort nie ein
                  Sonnenschutz aktiv.
                </td>
              </tr>
              <tr>
                <td>Neigung der Dachöffnung</td>
                <td>Nur bei Dachoberlichtern.</td>
              </tr>
              <tr>
                <td>Verglasung</td>
                <td>
                  Lichttransmissionsgrad der Scheibe. Zwischen Einfach-, Zweifach- und
                  Dreifachverglasung ist der Unterschied gering; deutlich wird er erst
                  bei Sonnenschutzglas.
                </td>
              </tr>
              <tr>
                <td>Sonnen-/Blendschutz</td>
                <td>
                  Vom reinen Blendschutz bis zu lichtlenkenden Systemen. An
                  Bildschirmarbeitsplätzen ist ein Blendschutz vorgeschrieben — daher
                  die Vorgabe.
                </td>
              </tr>
              <tr>
                <td>Wartungsfaktor</td>
                <td>
                  Berücksichtigt Alterung und Verschmutzung bis zur nächsten Wartung.
                  Vorgabe 0,67; 0,80 für sehr saubere, häufig gereinigte Anlagen, 0,60
                  bzw. 0,50 bei starker Staubbelastung und langen Wartungsintervallen.
                </td>
              </tr>
              <tr>
                <td>Regelung (Altverfahren)</td>
                <td>
                  Nur bei Datensätzen sichtbar, die mit der alten Rechenmethode angelegt
                  wurden — dort wird sie weiter verwendet. Für neue Datensätze ist sie
                  ohne Wirkung und wird nicht mehr abgefragt.
                </td>
              </tr>
            </tbody>
          </table>

          <table>
            <caption>Finanzielle Parameter</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Demontagekosten (Referenz)</td>
                <td>
                  Gesamtkosten der Demontagekosten inkl. Arbeitszeit, 
                  Maschinenkosten und Entsorgungskosten.
                </td>
              </tr>
              <tr>
                <td>Kosten Neuanlage (Geplant)</td>
                <td>Gesamte Anschaffungskosten bis hin zur Inbetriebnahme.</td>
              </tr>
              <tr>
                <td>Betriebs- und Instandhaltungskosten</td>
                <td>
                  Jährliche Kosten für Betriebsmaterial und
                  Instandhaltungsarbeiten (ohne Energiekosten).
                </td>
              </tr>
            </tbody>
          </table>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Alte und neue Rechenmethode</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            Die Beleuchtung kennt zwei Rechenmethoden. Welche gilt, entscheidet sich
            beim Anlegen eines Datensatzes und ändert sich danach nicht mehr — ein
            einmal übergebener Bericht behält seine Zahlen.
          </p>

          <h3>Neue Rechenmethode — DIN V 18599-4</h3>
          <p>
            Neue Datensätze rechnen nach DIN&nbsp;V&nbsp;18599-4:2018-09. Die Betriebszeit
            wird dabei in <strong>Tag- und Nachtstunden</strong> getrennt und mit drei
            Faktoren gewichtet:
          </p>
          <ul>
            <li>
              <strong>Präsenz</strong> — abhängig von der relativen Abwesenheit der
              Nutzungszone. Wirkt auf die gesamte Betriebszeit.
            </li>
            <li>
              <strong>Tageslicht</strong> — wirkt <em>nur</em> auf die Tagstunden und
              nur auf den tageslichtversorgten Flächenanteil.
            </li>
            <li>
              <strong>Konstantlicht</strong> — abhängig vom Wartungsfaktor, nur bei
              regelbarem Betriebsgerät.
            </li>
          </ul>
          <p>
            Die Nutzungsstunden und die Abwesenheit je Zone stammen aus
            DIN&nbsp;V&nbsp;18599-10:2018-09, Anhang&nbsp;A. Zwölf Zonen haben dort kein
            Gegenstück — die Schichtvarianten der Industriezonen, beide Parkplatzzonen
            und „Sonstige“. Für sie verlangt DIN&nbsp;V&nbsp;18599-4, 5.3, die
            Betriebszeiten <em>gesondert zu ermitteln</em>: mit der Option
            <strong>„Zonenprofil manuell“</strong> werden Tag- und Nachtstunden,
            F<sub>t</sub> und C<sub>A</sub> selbst eingetragen. Das Ergebnis vermerkt
            dann, dass das Profil nicht aus der Norm stammt. Ohne diese Eingabe bleibt
            die Zone für neue Datensätze gesperrt — geschätzte Stunden wären schlechter
            als gar keine, weil sie unbemerkt in einen Bericht liefen.
          </p>

          <h3>Alte Rechenmethode</h3>
          <p>
            Datensätze, die vor der Umstellung angelegt wurden, rechnen unverändert
            weiter: Brenndauer mal einem einzelnen Regelungsfaktor. Bei ihnen bleibt das
            Feld „Regelung“ sichtbar, aber schreibgeschützt.
          </p>
          <p>
            Der Unterschied ist nicht nur formal. Die alte Methode senkte mit dem
            Regelungsfaktor die <em>gesamte</em> Jahresbrenndauer — eine
            Tageslichtabschaltung sparte damit rechnerisch auch nachts. Sie setzte
            außerdem für jede Zone denselben Faktor an, während die Einsparung durch
            einen Präsenzmelder tatsächlich stark von der Nutzung abhängt.
          </p>

          <h3>Wenn Bestand und Planung verglichen werden</h3>
          <p>
            Eine Maßnahme vergleicht zwei Datensätze. Stammen beide aus derselben
            Methode, ist der Vergleich unmittelbar belastbar. Wird einer
            Bestandsvariante aus der alten Methode eine neue Planvariante
            gegenübergestellt, werden zwei Verfahren verglichen — die Differenz enthält
            dann auch den Methodenwechsel. In diesem Fall ist es sauberer, beide
            Varianten neu anzulegen.
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Berechnungsgrundlage und Daten</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <h3>Leuchten und Lampen</h3>
          <p>
            Mit Hilfe der Methode 2 der Beleuchtung können Sie eine detaillierte Berechnung 
            durchführen. Geben Sie eine Bezeichnung für die zu betrachtende 
            Zone und die dazugehörigen erforderlichen Informationen ein. Sie haben die Auswahl 
            verschiedener Zonen, von denen die Brenndauer (diese kann auch manuell angepasst 
            werden) und die verschiedenen Beleuchtungsmöglichkeiten abhängig sind.  
          </p>
          <p>
            Es besteht die Auswahl zwischen einem Leuchtmittel- oder einem Leuchtensystemtausch. 
            Bei einem Leuchtmitteltausch wird nur das Leuchtmittel (die Lampe) ausgetauscht, 
            bei einem Leuchtensystemtausch wird die komplette Leuchte (z.B. Deckenleuchte) 
            inkl. Leuchtmittel getauscht. 
          </p>
          <p>
            Die Auswahl der Leuchte wird beschränkt mit der Art des Austauschs. Verschiedene
            Vorschaltgeräte können für eine Bestands- oder Austauschvariante ausgewählt werden.
            Die Bestandsgeräte stehen in jeder Variante zur Auswahl; die steuerbaren Geräte
            kommen beim Leuchtensystemtausch hinzu. Welche Geräte eine bestimmte Leuchte
            zulässt, entscheidet der Leuchtenkatalog — die Auswahlliste im Formular zeigt
            nur die zulässigen.
          </p>
          <!-- Aus `/methods/lighting2/ballast/` statt von Hand: die frühere Liste war
               schon auseinandergelaufen und führte PSR als „regelbares, elektronisches
               Betriebsgerät“, während der Datensatz „Regelbarer LED Treiber“ sagt. -->
          <template v-if="ballastGroups.length">
            <template v-for="group in ballastGroups" :key="group.title">
              <p>{{ group.title }}:</p>
              <v-table density="compact" class="mb-4">
                <thead>
                  <tr>
                    <th>Kürzel</th>
                    <th>Bezeichnung</th>
                    <th>Verlustleistung</th>
                    <th>Steuerbarkeit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="b in group.items" :key="b.id">
                    <td>{{ b.name }}</td>
                    <td>{{ b.full_name }}</td>
                    <td>{{ ballastPowerNote(b) }}</td>
                    <td>{{ ballastControlNote(b) }}</td>
                  </tr>
                </tbody>
              </v-table>
            </template>
            <p>
              Die Verlustleistung gilt <strong>je Gerät</strong> und wird mit der Anzahl
              der Vorschaltgeräte je Leuchte multipliziert auf die Lampenleistung addiert.
              Bei LED-Treibern steckt der Treiberverlust bereits in der katalogisierten
              Systemleistung der Leuchte und wird deshalb kein zweites Mal angesetzt.
            </p>

            <h3>Systemleistung einer Leuchte</h3>
            <p>
              DIN V 18599-4, 5.4.5 definiert die Systemleistung als „die gesamte
              elektrische Leistung aller Lampenstromkreise in der Leuchte“ und nennt drei
              Wege, sie zu bestimmen — in dieser Rangfolge:
            </p>
            <ol>
              <li>
                <strong>Laut Hersteller oder Messung.</strong> Der Vorzugsweg: „Sofern
                genaue Herstellerangaben zur Systemleistung der Leuchten vorliegen, sind
                diese zu verwenden. Die Messung der Systemleistung ist ebenfalls
                zulässig.“ Die Angabe enthält Lampen und Betriebsgerät; es wird nichts
                mehr hinzugerechnet.
              </li>
              <li>
                <strong>Tabelle 8 (k<sub>BG</sub>).</strong> Die „schnelle und
                vereinfachte Ermittlung“: Lampenleistung × Faktor. 1,1 für
                Leuchtstofflampen mit EVG, 1,3 mit KVG (VVG gilt der Norm als
                gleichwertig), 1,1 für Niedervolt-Halogen mit Transformator sowie für
                Metallhalogendampf- und Quecksilberdampf-Hochdruck mit KVG. Für LED führt
                die Tabelle keine Zeile — dort gilt die Leuchtenleistung (5.4.3).
              </li>
              <li>
                <strong>Lampenleistung plus Verlustleistung je Gerät.</strong> Das
                gewachsene EARNS-Verfahren und weiterhin die Vorgabe, damit gespeicherte
                Ergebnisse unverändert bleiben. Es ist keiner der beiden Normwege.
              </li>
            </ol>
            <p>
              Die drei Wege liefern spürbar verschiedene Zahlen. Am Datenblatt eines
              Philips HF-P 258 TL-D III (zwei T8-Lampen à 58 W, Systemleistung 105,5 W)
              gemessen:
            </p>
            <v-table density="compact" class="mb-4">
              <thead>
                <tr>
                  <th>Weg</th>
                  <th>Ergebnis</th>
                  <th>Abweichung</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Datenblatt</td>
                  <td>105,5 W</td>
                  <td>—</td>
                </tr>
                <tr>
                  <td>Verlustleistung je Gerät (Vorgabe)</td>
                  <td>120,0 W</td>
                  <td>+13,7 %</td>
                </tr>
                <tr>
                  <td>Tabelle 8</td>
                  <td>127,6 W</td>
                  <td>+20,9 %</td>
                </tr>
              </tbody>
            </v-table>
            <p>
              Beide Rechenwege liegen darüber, weil sie von der <em>Nennleistung</em>
              2 × 58 W ausgehen; am HF-Vorschaltgerät ziehen dieselben Lampen aber nur
              98 W. Die Faktoren der Tabelle 8 „beziehen sich auf mittlere
              Lampenleistungen im Leistungsbereich“ und werden an den Rändern
              entsprechend grob. Wo ein Datenblatt vorliegt, ist es deshalb nicht nur der
              normkonforme, sondern auch der genauere Weg.
            </p>
          </template>
          <p v-else>
            Die Übersicht der Vorschaltgeräte konnte nicht geladen werden. Die Auswahlliste
            im Formular nennt Kürzel, Bezeichnung und Verlustleistung jedes Geräts.
          </p>
          <p>
            Die Anzahl der Vorschaltgeräte je Leuchte steht von 1 bis zur Anzahl der 
            Lampen je Leuchte zur Verfügung. Beispiel: Bei einer 3-flammigen Leuchte 
            stehen 1 bis 3 Vorschaltgeräte je Leuchte zu Auswahl. Bitte zählen Sie die 
            Vorschaltgeräte durch, nicht immer ist die Anzahl der Lampen je Leuchte 
            gleich der Anzahl der Vorschaltgeräte!
          </p>
          <p>
            Verschiedene Steuerungsmöglichkeiten werden in der Applikation zur Auswahl 
            gestellt. Es kann ausgewählt werden, ob schon eine Steuerungsvariante im 
            Bestand vorhanden ist oder ob eine Steuerung für den Austausch vorgesehen 
            ist. Je nach ausgewählter Steuerung wird die Brenndauer mit einem Faktor 
            multipliziert. 
          </p>
          <p>
            Folgende Steuerungsmöglichkeiten stehen insgesamt zur Auswahl:
          </p>
          <ul>
            <li>
              kein Präsenzmelder, keine Tageslichtabschaltung	1
            </li>
            <li>
              mit Präsenzsteuerung 0,75
            </li>
            <li>
              mit Tageslichtabschaltung 0,7
            </li>
            <li>
              mit Präsenzsteuerung und Tageslichtabschaltung 0,6
            </li>
          </ul>
          <p>
            Nicht immer stehen alle Steuerungsmöglichkeiten zur Auswahl, sie sind 
            abhängig von dem Vorschaltgerät, welches eingesetzt werden soll.
          </p>
          <p>
            Die regelbaren EVGs bzw. LED Treiber lassen sich sowohl mit 
            einer Präsenzsteuerung als auch mit einer Tageslichtsteuerung koppeln:
          </p>
          <ul>
            <li v-for="b in dimmableBallasts" :key="b.id">
              {{ b.name }} – {{ b.full_name }}
            </li>
          </ul>
          <p>
            Die schaltbaren EVGs bzw. LED Treiber sind nur für eine Präsenzsteuerung geeignet:
          </p>
          <ul>
            <li v-for="b in switchableBallasts" :key="b.id">
              {{ b.name }} – {{ b.full_name }}
            </li>
          </ul>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Hinterlegte Austauschlösungen</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text eager>
          <p>
            In diesem Abschnitt erhalten Sie einen Überblick über die hinterlegten 
            Austauschlösungen. Sie haben beim Austausch die Möglichkeit auszuwählen, 
            ob Sie nur die Leuchtmittel oder das gesamte Leuchtensystem (Leuchte 
            inkl. Lampe) austauschen möchten. Für beide Möglichkeiten werden die 
            hinterlegten Technologien erläutert. Bei den hinterlegten 
            handelt es sich um einen Auszug der gängigen Lösungen. 
          </p>
          <h2>Leuchtmitteltausch</h2>
          <p>
            Bei einem Leuchtmitteltausch wir nur das Leuchtmittel getauscht. 
            Die Leuchte bleibt dabei unverändert. Folgende Leuchtmittel sind für den Austausch 
            hinterlegt:
          </p>
          <p>
            Bestand:
          </p>
          <ul>
            <li>
              NV-Halogensport
            </li>
            <li>
              T5-Leuchtstofflampe
            </li>
            <li>
              T8-Leuchtstofflampe
            </li>
          </ul>
          <p>
            Geplant:
          </p>
          <ul>
            <li>
              LED
            </li>
            <li>
              T8 LED-Leuchtstofflampe
            </li>
            <li>
              T5 Power Saver Set
            </li>
            <li>
              T5 Leuchtstofflampe
            </li>
            <li>
              T5 TLD-Eco
            </li>
          </ul>
          <h3>NV-Halogenspot</h3>
          <img
            :src="image004jpg"
            alt="NV-Halogenspot"
          />
          <figcaption>
            Abbildung: NV-Halogenspot
          </figcaption>
          <h2>Leuchtsystemtausch</h2>
          <p>
            Anders als beim Leuchtmitteltausch wird beim Leuchtensystemtausch 
            die Leuchte inkl. Leuchtmittel getauscht. Je nach Anwendungsfall 
            stehen verschiedene Möglichkeiten zur Verfügung. Folgende 
            Austauschkombinationen sind für den Leuchtmitteltausch in EANRS hinterlegt:
          </p>
          <ul>
            <li>
              T5-Anbauleuchte – LED Anbauleuchte
            </li>
            <li>
              T8-Anbauleuchte – T5-Anbauleuchte
            </li>
            <li>
              T8-Einlegeleuchte – T5-Einlegeleuchte/LED-Einlegeleuchte
            </li>
            <li>
              Downlight mit Kompaktleuchtstoffröhren – LED-Downlight
            </li>
            <li>
              Einbaustrahler (Spots) – LED-Einbaustrahler (LED-Spots)
            </li>
            <li>
              Wand- und Deckenleuchte – LED-Wand- und Deckenleuchte
            </li>
            <li>
              Konv. Feuchtraumleuchte – LED-Feuchtraumleuchte
            </li>
            <li>
              Konv. Lichtleiste – LED-Lichtleiste
            </li>
            <li>
              Konv. Lichtband – LED-Lichtband
            </li>
            <li>
              Konv. Hallenreflektorleuchte – LED-Hallenleuchte
            </li>
            <li>
              Außenleuchte – LED-Außenleuchte
            </li>
          </ul>
          <img
            :src="image004jpg"
            alt="T8-Leuchtstoffröhre"
          />
          <figcaption>
            Abbildung: T8-Leuchtstoffröhre 
          </figcaption>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Zusätzliche Informationen</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text eager>
          <h2>Lichttechnische Grundbegriffe </h2>
          <img
            :src="lightingpng"
            alt="Hängelampe"
          />
          <figcaption>
            Abbildung: Beispiel Hängelampe im Büro
          </figcaption>
          <h3>Leuchte</h3>
          <p>
            Der gesamte Beleuchtungskörper inklusive aller für Befestigung, Betrieb
            und Schutz der Lampe notwendigen Komponenten ist die &bdquo;Leuchte&ldquo;
            &ndash; im Volksmund auch gerne als &bdquo;Lampe&ldquo; bezeichnet. Die
            Leuchte schützt die Lampe, verteilt und lenkt deren Licht, verhindert,
            dass es blendet.
          </p>
          <p>Leuchten werden unterschieden nach</p>
          <ul>
            <li>
              Art der eingesetzten Lampen (für Energiesparlampen, für
              Leuchtstofflampen, für Entladungslampen),
            </li>
            <li>
              der Anzahl der Lampen (ein-, zweilampig bzw. ein-, zweiflammig, usw.),
              nach dem Einsatzbereich (Innenleuchten, Außenleuchten),
            </li>
            <li>dem Einsatzort (Schreibtischleuchten, Badleuchten usw.),</li>
            <li>
              der Schutzart (Leuchten für trockene, feuchte und staubige Räume),
            </li>
            <li>
              der Bauart (offene Leuchten, geschlossene Leuchten, Reflektorleuchten,
              Spiegelleuchten, Rasterleuchten, Wannenleuchten, Scheinwerfer),
            </li>
            <li>der Montageart (Wand-, Decken-, Pendel-, oder Handleuchten),</li>
            <li>
              dem Verwendungszweck (technische Leuchten, Wohnraumleuchten, dekorative
              Leuchten oder Effektleuchten) oder
            </li>
            <li>
              nach der Beleuchtungsaufgabe (Arbeitsplatzleuchten, Straßenleuchten).
            </li>
          </ul>

          <h3>Lampe </h3>
          <p>
            Ohne Lampe kein Licht: &bdquo;Lampe&ldquo; bezeichnet die technische
            Ausführung einer künstlichen Lichtquelle. Die Lampe wandelt elektrische
            Energie in Licht um. Die Lampe wird in der Leuchte eingesetzt, die das
            Licht der Lampe verteilt, lenkt und vor Blendung schützt.
          </p>
          <p>
            Lampen gibt es in vielen Ausführungen. Sie unterscheiden sich durch die
            Art der Lichterzeugung, ihre geometrische Form und ihre lichttechnischen
            Eigenschaften. Im Wohnbereich werden zum Beispiel Halogenlampen,
            Energiesparlampen und Leuchtstofflampen eingesetzt.
          </p>

          <h3>Vorschaltgerät</h3>
          <p>
            Für den Betrieb von Entladungslampen &ndash; zum Beispiel
            Leuchtstofflampen &ndash; sind Vorschaltgeräte (VGs) erforderlich. Denn
            nach der Zündung dieser Lampen erfolgt eine lawinenartige Ionisierung der
            enthaltenen Edelgase oder Metalldämpfe, die zu einem starken Anstieg des
            Lampenstroms führt. Er würde die Lampe nach kurzer Zeit zerstören. Daher
            begrenzt ein VG den Lampenstrom, auch im Betrieb.
          </p>
          <p>
            Zu unterscheiden sind konventionelle (KVGs) und verlustarme (VVGs)
            Vorschaltgeräte sowie elektronische (EVGs) Vorschaltgeräte. EVG für
            Leuchtstofflampen gibt es in dimmbarer und nicht dimmbarer Ausführung. Bei
            Energiesparlampen sind VG im Schraubsockel integriert, ansonsten sind sie
            Bestandteil der Leuchte.
          </p>

          <h3>Lichtstrom in Lumen (lm) </h3>
          <p>
            Der Lichtstrom &Phi; &ndash; gemessen in Lumen (lm) &ndash; ist die
            Lichtleistung einer Lampe. Er beschreibt die von der Lichtquelle in alle
            Richtungen abgestrahlte Leistung im sichtbaren Bereich. Eine Glühlampe hat
            etwa 1.400 Lumen, eine zweiseitig gesockelte Halogenlampe bis zu 44.000
            Lumen. Der Lichtstrom einzelner LEDs beträgt zwischen 18 und 30 Lumen.
          </p>
          <p>
            Gerade im Bereich der LEDs ist die Angabe des Lichtstroms von großer
            Bedeutung. Oftmals werden LEDs auf den Markt gebracht, die eine geringere
            Leistungsaufnahme im Vergleich zu Konkurrenzprodukten haben, jedoch auch
            einen viel geringeren Lichtstrom. Dadurch wird ein
            &bdquo;schwächeres&ldquo; Licht erzeugt. Es ist daher wichtig, diese
            Angabe neben der Leistungsaufnahme ebenfalls zu vergleichen.
          </p>
          <img :src="lightinglichtstrompeg" />
          <figcaption>
            Abbildung: Visualisierung des Lichtstroms 
          </figcaption>
          <h3>Beleuchtungsstärke in Lux (lx) </h3>
          <p>
            Die Beleuchtungsstärke (Kurzzeichen: E, Maßeinheit: Lux) definiert, wie
            viel Licht &ndash; lichttechnisch genauer wie viel Lichtstrom (in Lumen)
            &ndash; auf eine bestimmte Fläche fällt: Sie beträgt ein Lux, wenn der
            Lichtstrom von einem Lumen einen Quadratmeter Fläche gleichmäßig
            ausleuchtet. Ein Beispiel: Eine normale Kerzenflamme hat im Abstand von
            einem Meter ungefähr ein Lux Beleuchtungsstärke.
          </p>
          <p>
            Die Beleuchtungsstärke wird mit einem Luxmeter auf horizontalen und
            vertikalen Flächen gemessen. Sie beschreibt allerdings nicht präzise den
            Helligkeitseindruck eines Raumes, da dieser wesentlich von den
            Reflexionseigenschaften der Raumflächen abhängt. So erscheint ein weißer
            Raum bei gleicher Beleuchtungsstärke heller als ein dunkler. Siehe auch:
            halbzylindrische Beleuchtungsstärke.
          </p>
          <p>
            Weil die Lichtverteilung einer normalen Beleuchtung nicht absolut
            gleichmäßig ist, geben Normen in der Regel die mittlere Beleuchtungsstärke
            an. Sie berücksichtigt die Ungleichmäßigkeiten rechnerisch: Die mittlere
            Beleuchtungsstärke ist das gewichtete arithmetische Mittel aller
            Beleuchtungsstärken im Raum.
          </p>
          <div class="row">
            <div class="column">
              <img
                :src="lightingbeleuchtungsstaerkepng"
                alt="Beläuchtungstärke"
                style="width:75%"
              />
            </div>
            <div class="column">
              <img
                :src="lightingbeleuchtungsstaerkeaxpng"
                alt="Beleuchtungsachsen"
                style="width:75%"
              />
            </div>
          </div>
          <figcaption>
            Abbildung: Beleuchtungsstärke (links) und gewichtete arithmetische Mittel aller Beleuchtungsstärken im Raum (rechts)
          </figcaption>

          <h3>Lichtstärke in Candela (cd) </h3>
          <p>
            Die Lichtstärke (Kurzzeichen: I) ist der Teil des Lichtstroms, der in eine
            bestimmte Richtung strahlt. Sie wird in Candela (cd) gemessen.
          </p>
          <p>
            Die räumliche Verteilung der Lichtstärke (Lichtstärkeverteilung)
            charakterisiert die Lichtausstrahlung von Leuchten und Reflektorlampen.
            Die Lichtstärkeverteilung wird grafisch in Form von Kurven dargestellt,
            den Lichtstärkeverteilungskurven (LVK) &ndash; siehe Lichtstärkeverteilung
          </p>
          <img :src="lightinglichtstaerkepng" />
          <figcaption>
            Abbildung: Visualisierung der Lichtstärke
          </figcaption>
          <h3>Lichtstärkeverteilung </h3>
          <p>
            Die Lichtstärkeverteilung beschreibt die räumliche Verteilung der
            Lichtstärke. Form und Symmetrie der Lichtstärkeverteilung kennzeichnen
            tief- und breitstrahlende bzw. symmetrisch und asymmetrisch strahlende
            Leuchten oder Reflektorlampen.
          </p>
          <p>
            Zur Darstellung dient die Lichtstärkeverteilungskurve (LVK): Sie entsteht,
            wenn die in Polarkoordinaten dargestellten Lichtstärken einer Leuchte oder
            Reflektorlampe in ihren verschiedenen Ausstrahlungsrichtungen miteinander
            zu einem Kurvenzug verbunden werden.
          </p>
          <img
            :src="lightinglichtstaerkeverteilungpng"
          />
          <figcaption>
            Abbildung: Visualisierung der Lichtstärkeverteilung
          </figcaption>
          <h3>
            Lichtausbeute in Lumen/Watt (lm/W) 
          </h3>
          <p>
            Die Lichtausbeute ist das Maß für die Wirtschaftlichkeit einer Lampe. Sie
            sagt aus, wie viel Lichtstrom in Lumen (lm) pro Watt (W) ihrer
            Leistungsaufnahme eine Lampe erzeugt. Je höher das Verhältnis Lumen/Watt,
            desto besser setzt eine Lampe die eingebrachte Energie in Licht um. Einige
            Beispiele:
          </p>
          <ul>
            <li>Allgebrauchsglühlampe 12 lm/W,</li>
            <li>Halogenlampe 20 lm/W,</li>
            <li>Energiesparlampe 60 lm/W,</li>
            <li>stabförmige Leuchtstofflampe 90 lm/W</li>
            <li>LEDs 100-120 lm/W.</li>
          </ul>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Quellen</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ul>
            <li>
              <strong>DIN V 18599-4:2018-09</strong> — Nutz- und Endenergiebedarf für
              Beleuchtung. Rechenweg und Faktortabellen der neuen Rechenmethode.
            </li>
            <li>
              <strong>DIN V 18599-10:2018-09</strong>, Anhang A — Nutzungsrandbedingungen:
              Tag- und Nachtstunden, Wartungswert der Beleuchtungsstärke und relative
              Abwesenheit je Nutzungszone.
            </li>
            <li>
              <strong>DIN EN 12464-1</strong> — Beleuchtung von Arbeitsstätten; Herkunft
              des Wartungsfaktors.
            </li>
            <li>
              Die Leuchten- und Lampendaten sowie die Regelungsfaktoren der
              <em>alten</em> Rechenmethode stammen aus dem Philips
              Energy-Quickscantool (2012) und nicht aus einer Norm. Für die alte Methode
              gilt das unverändert weiter.
            </li>
            <li>
              Die Definitionen und Abbildungen des Kapitels zu den lichttechnischen
              Grundbegriffen sind der Webseite
              <a href="http://www.licht.de">http://www.licht.de</a> entnommen. Dort
              erhalten Sie auch weiterführende Begriffserklärungen.
            </li>
            <li>
              Weitere Informationen über die verschiedenen Austauschlösungen erhalten Sie auf
              <a href="http://www.lighting.philips.de">http://www.lighting.philips.de</a>.
            </li>
          </ul>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import api from '@/plugins/axios'
import {
  ballastPowerNote,
  ballastControlNote,
  ballastGroup,
  BALLAST_GROUP_LEGACY,
  BALLAST_GROUP_CONTROLLABLE,
  type Ballast,
} from '@/utils/ballast'
import image004jpg from '@/assets/help/lighting/image004.jpg'
import lightingpng from '@/assets/help/lighting/lighting.png'
import lightinglichtstrompeg from '@/assets/help/lighting/lighting_lichtstrom.png'
import lightingbeleuchtungsstaerkepng from '@/assets/help/lighting/lighting_beleuchtungsstaerke.png'
import lightingbeleuchtungsstaerkeaxpng from '@/assets/help/lighting/lighting_beleuchtungsstaerke_ax.png'
import lightinglichtstaerkepng from '@/assets/help/lighting/lighting_lichtstärke.png'
import lightinglichtstaerkeverteilungpng from '@/assets/help/lighting/lighting_lichtstaerkeverteilung.png'

/* Das Glossar der Vorschaltgeräte kommt aus derselben Quelle wie die Auswahlliste im
   Formular. Von Hand gepflegt lief es auseinander, sobald ein Datensatz umbenannt wurde. */
const ballasts = ref<Ballast[]>([])

const ballastGroups = computed(() => {
  const groups = [BALLAST_GROUP_LEGACY, BALLAST_GROUP_CONTROLLABLE]
  return groups
    .map(title => ({
      title,
      items: ballasts.value.filter(b => ballastGroup(b) === title),
    }))
    .filter(group => group.items.length > 0)
})

/* Welche Steuerung ein Gerät tragen kann, steht als Flag am Datensatz und ist dieselbe
   Grundlage, auf der das Formular die Auswahl einschränkt (WP-08 / Befund V1). */
const dimmableBallasts = computed(() => ballasts.value.filter(b => b.dimmable))
const switchableBallasts = computed(() => ballasts.value.filter(b => b.switchable))

/* Schlägt der Abruf fehl, bleibt die Liste leer und die Hilfeseite zeigt den Ersatztext.
   Eine Hilfeseite darf an einer fehlenden Tabelle nicht scheitern. */
onMounted(async () => {
  try {
    const { data } = await api.get('/methods/lighting2/ballast/')
    ballasts.value = data
  } catch {
    ballasts.value = []
  }
})
</script>

<style scoped>
</style>